import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Input, Tabs, message, Form, Row, Col } from "antd";
import { EmailSelect } from "./EmailSelect";
import { validateEmailOrUid } from "../../../../utils/validateEmail";
import {
  EmailSubmitAction,
  emailSubmitStore,
  MailInitialRecipients,
  submitMessageMap,
} from "../Stores/EmailSubmitStore";
import { observer } from "mobx-react";
import { ReceiverCheckbox } from "../Components/EmailSendFormTopActions/ReceiverCheckbox";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TopBar } from "../Components/EmailSendFormTopActions/TopBar";
import { EmailAttachmentsTable } from "../Components/EmailAttachmentsTable";
import { FormInstance } from "antd/lib/form";
import { Button } from "Components/General/Button";
import { MailType } from "./EmailSendModal";
import classNames from "classnames";
import Text from "antd/lib/typography/Text";
import { EmailTextEditor, EmailTextEditorInstance } from "./EmailTextEditor";
import { modal } from "Components/General/Modal/AntdModal";
import { userInfoStore } from "Stores";
import { WarningTwoTone } from "@ant-design/icons";
import { compactObject } from "utils/compactObject";

export enum EmailRecipientViewMode {
  BY_GROUP = "GROUP",
  BY_PERSON = "PERSON",
}

export interface EmailRecipientView {
  mode: EmailRecipientViewMode;
  exceptedGroupId?: number[];
}

interface EmailSendFormProps {
  isLoading: boolean;
  setConfirmOnCancel: (b: boolean) => void;
  initialData?: API_MailResponse & MailInitialRecipients;
  initialDataType?: MailType;
  mailSubmitAction?: EmailSubmitAction;
  onSubmit: (o: API_MailRequest, i: EmailSubmitAction) => Promise<void>;
  toggleFilesModal: () => void;
}

interface EmailSendFormState {
  cc: boolean;
  bcc: boolean;
  isMessageInvalid: boolean;
  initialized: boolean;
}

type EmailSendFormValues = Partial<API_MailRequest>;

const buildMailRequestPayload = ({
  values,
  content,
  draft,
  initialData,
  initialDataType,
  mailSubmitAction,
}: {
  values: EmailSendFormValues;
  content: string;
  draft: boolean;
  initialData?: (API_MailResponse & MailInitialRecipients) | undefined;
  initialDataType?: MailType | undefined;
  mailSubmitAction?: EmailSubmitAction | undefined;
}): { postObject: API_MailRequest; action: EmailSubmitAction; shouldConfirmNoSubject: boolean } => {
  const cleanedValues = draft ? values : (compactObject(values as Record<string, unknown>) as EmailSendFormValues);

  // Need to set the initial "type" manually to keep track of the draft
  const initialType = initialData?.type;
  const baseObject: API_MailRequest = initialType
    ? { ...cleanedValues, body: content, type: initialType }
    : { ...cleanedValues, body: content };

  const isDraftEditMode = initialDataType === "draft" && Boolean(initialData?.id);
  const hasSubject = Object.prototype.hasOwnProperty.call(cleanedValues, "subject");

  if (draft) {
    const action: EmailSubmitAction = mailSubmitAction ?? "save_draft";
    const postObject: API_MailRequest = { ...baseObject, type: "DRAFT" };
    if (isDraftEditMode && initialData?.id) {
      postObject.id = initialData.id;
    }
    return { postObject, action, shouldConfirmNoSubject: !hasSubject };
  }

  // When sending an existing draft, it needs additional meta
  if (baseObject.type === "DRAFT" && mailSubmitAction !== "copy_draft" && initialData?.id) {
    const postObject: API_MailRequest = {
      ...baseObject,
      previousType: "DRAFT",
      type: "SENT",
      id: initialData.id,
    };
    return { postObject, action: "sent", shouldConfirmNoSubject: !hasSubject };
  }

  return {
    postObject: { ...baseObject, type: "SENT" },
    action: "sent",
    shouldConfirmNoSubject: !hasSubject,
  };
};

export const EmailSendForm = observer(
  ({
    isLoading,
    setConfirmOnCancel,
    initialData,
    initialDataType,
    mailSubmitAction,
    onSubmit,
    toggleFilesModal,
  }: EmailSendFormProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const editorRef = useRef<EmailTextEditorInstance>(null);
    const formRef = useRef<FormInstance>(null);
    const initialEditorData = useMemo(
      () => ({
        ...initialData,
        ...initialData?.initialRecipients,
      }),
      [initialData]
    ) as API_MailResponse;
    const [state, setState] = useState<EmailSendFormState>({
      cc: false,
      bcc: false,
      isMessageInvalid: false,
      initialized: false,
    });

    const onValuesChange = useCallback(
      (changedValues) => {
        if (Object.keys(changedValues).length > 0) {
          setConfirmOnCancel(true);
        }
        if (editorRef.current?.returnContent()) {
          setState((prevState) => ({ ...prevState, isMessageInvalid: false }));
        }
      },
      [setConfirmOnCancel]
    );

    const validateHTML = useCallback((htmlString: string) => {
      const isInvalid = htmlString.length === 0;
      setState((prevState) => ({ ...prevState, isMessageInvalid: isInvalid }));
      return isInvalid;
    }, []);

    const handleSubmit = useCallback(
      async (values: EmailSendFormValues, draft = false) => {
        if (emailSubmitStore.externalFilesLoading) {
          message.warning(t("Email submit is impossible. Some attachments did not finish loading."));
          return;
        }
        const content = editorRef.current?.returnContent() ?? "";
        if (!validateHTML(content)) {
          const { postObject, action, shouldConfirmNoSubject } = buildMailRequestPayload({
            values,
            content,
            draft,
            initialData,
            initialDataType,
            mailSubmitAction,
          });

          try {
            if (shouldConfirmNoSubject) {
              const messagesMap = submitMessageMap[action];
              modal.confirm({
                title: `${t(messagesMap.subject)}?`,
                zIndex: 1011,
                okText: t(messagesMap.okLabel),
                cancelText: t(messagesMap.cancelLabel),
                onOk: async () => await onSubmit(postObject, action),
              });
            } else {
              await onSubmit(postObject, action);
            }
          } catch (err) {
            throw err;
          }
        }
      },
      [initialData, initialDataType, onSubmit, validateHTML]
    );

    const handleBccFieldChange = useCallback(() => {
      if (state.bcc) {
        // Clear submit data when hiding fields
        emailSubmitStore.update({ bcc: [] } as API_MailRequest);
        formRef.current?.setFieldsValue({ bcc: [] });
      }
      setState((prevState) => ({ ...prevState, bcc: !prevState.bcc }));
    }, [state.bcc]);

    const checkEmail = useCallback(
      (rule, values) => {
        if (values?.some((email) => !validateEmailOrUid(email))) {
          const msg = t("Please enter a valid email address");
          return Promise.reject(msg);
        } else {
          return Promise.resolve();
        }
      },
      [t]
    );

    const getInitialBody = useCallback(() => {
      const { body } = initialData || {};

      let content: string = "";
      if (body) {
        content = content + `<p>>>></p>${body}<p><<<</p>\n`;
      }
      if (!content.length) {
        content = " ";
      }
      return content;
    }, [initialData]);

    const attachmentsLabel = useMemo(() => {
      let label = t("Attachments");
      if (emailSubmitStore.fileList.length) {
        label += ` (${emailSubmitStore.fileList.length})`;
      }
      return label;
    }, [emailSubmitStore.fileList.length]);

    const buttonText = useMemo(() => {
      const button: { [key in MailType]?: string } = {
        reply: "Reply",
        attach: "Send",
        draft: "Send",
        forward: "Forward",
        reply_all: "Answer all",
      };
      return initialDataType ? button[initialDataType] : "Send";
    }, [initialDataType]);

    const draftButtonText = useMemo(() => {
      const labelMap: { [key in EmailSubmitAction]?: string } = {
        save_draft: "Save draft",
        edit_draft: "Edit draft",
        copy_draft: "Copy draft",
      };
      return labelMap[mailSubmitAction] || labelMap["save_draft"];
    }, [mailSubmitAction]);

    const initialValues = useMemo(
      () => ({
        cc: initialDataType === "reply_all" || initialDataType === "draft" ? initialData?.cc?.slice() : [],
        bcc: initialDataType === "draft" ? initialData?.bcc?.slice() : [],
        subject:
          initialDataType && initialDataType.startsWith("reply") && initialData?.subject
            ? `Re: ${initialData.subject}`
            : initialDataType === "forward" && initialData?.subject
            ? `Fwd: ${initialData.subject}`
            : initialDataType === "draft"
            ? initialData?.subject
            : null,
        attachmentType: "LINK",
        body: getInitialBody(),
      }),
      [initialDataType, initialData, getInitialBody]
    );

    const mapAttachments = (attachments: API_FileAttachment[]) => {
      attachments.forEach((item) => {
        // if file comes from workflow transform to API_FileAttachment interface
        if ((item as API_FileInfo).fileName) {
          const originalFile: API_FileInfo = item;
          const fileInfoToAttacment: API_FileAttachment = {
            name: originalFile?.fileName,
            id: originalFile?.id,
            type: originalFile?.fileType,
            size: originalFile?.fileSize,
          };
          emailSubmitStore.addProjectFile(fileInfoToAttacment);
        } else {
          emailSubmitStore.addProjectFile(item);
        }
      });
    };

    /**
     * This method checks attachments that don't have access right "DOWNLOAD" because users should not forward
     * attachments that they don't have full access to them.
     */
    const checkAttachmentsAccess = () => {
      if (initialData.attachments && initialData.attachments.length) {
        const attachmentsWithoutDownloadAccess = initialData.attachments.some(
          // file was passed from documents table does not have accessLevel property -
          // check fileActions.download of "FileDataEntity"
          (item) => {
            if (!item.accessLevel) {
              if ((item as API_FileDataEntity).fileActions) {
                return !(item as API_FileDataEntity).fileActions.download;
              } else if (item as API_FileInfo) {
                return false;
              } else {
                return true;
              }
            } else {
              return item.accessLevel !== "DOWNLOAD";
            }
          }
        );

        if (attachmentsWithoutDownloadAccess) {
          const { viewOnly, downloadable } = initialData.attachments.reduce(
            (buffer, current) => {
              if (current.accessLevel !== "DOWNLOAD") {
                buffer.viewOnly.push(current);
              } else {
                buffer.downloadable.push(current);
              }
              return buffer;
            },
            { viewOnly: [], downloadable: [] }
          );
          // if "downloadable" array is empty - we still show this modal
          // because such use case wasn't described in DEV-3568
          const setOnlyDownloadable = () => {
            emailSubmitStore.removeAttachments();
            downloadable.forEach((item) => emailSubmitStore.addProjectFile(item));
          };

          modal.confirm({
            title: t("Some files may not be forwarded"),
            zIndex: 1011,
            icon: <WarningTwoTone twoToneColor="#faad14" />,
            width: "100%",
            style: { maxWidth: 600 },
            content: (
              <div>
                <ul>
                  {viewOnly.map((item) => (
                    <li key={item.id}>"{item.name}"</li>
                  ))}
                </ul>
                <div>{t("You need at least download rights to forward this email.")}</div>
              </div>
            ),
            okText: t("Forward allowed files"),
            onOk: setOnlyDownloadable,
            cancelButtonProps: { style: { float: "left" } },
            cancelText: t("Forward without any attachments"),
            onCancel: emailSubmitStore.removeAttachments,
          });
        }
      }
    };

    //componentDidUpdate
    useEffect(() => {
      const values = formRef.current?.getFieldsValue(["cc", "bcc"]);
      // Show Cc & Bcc fields if corresponding fields are present
      if (values?.cc && values.cc.length && !state.cc) {
        setState((prevState) => ({ ...prevState, cc: true }));
      }
      if (values?.bcc && values.bcc.length && !state.bcc) {
        setState((prevState) => ({ ...prevState, bcc: true }));
      }
    }, [state.cc, state.bcc]);

    //componentDidMount
    useEffect(() => {
      if (initialData) {
        let newFormData: API_MailRequest = {};
        newFormData = {
          body: initialData.body,
          from: initialData.from,
          subject: initialData.subject,
          to: initialData.to,
        };
        if (initialDataType === "reply_all") {
          // filter out current user from receivers
          const currentUserUid = `@user${userInfoStore.id}`;
          const filterCurrentUser = (recipients: string[]) =>
            recipients.filter((item) => item && item !== currentUserUid);
          newFormData = {
            ...newFormData,
            cc: initialData.cc,
            to: filterCurrentUser(newFormData.to),
          };
        }
        if (initialDataType === "draft") {
          if (initialData.attachments && initialData.attachments.length) {
            mapAttachments(initialData.attachments);
          }
          checkAttachmentsAccess();
          // This is a temporary fix to set the importance of the email to the initial importance of the email.
          // This is caused by PAVE-4393 implementation
          // This should be fixed by separate ticket.
          // Delete this comment after fixing the issue.
          newFormData.importance = initialData.importance;
        }
        if (initialDataType === "forward" || initialDataType === "attach") {
          newFormData.to = [];
          newFormData.cc = [];
          newFormData.bcc = [];
          newFormData.importance = initialData.importance;
          if (initialData.attachments && initialData.attachments.length) {
            mapAttachments(initialData.attachments);
          }
          checkAttachmentsAccess();
        }
        formRef.current?.setFieldsValue({ to: newFormData.to });
        emailSubmitStore.update(newFormData);
      }
      /**
       * without this tweak font-family and font-size dropdowns will not interact.
       * this might be a problem of wysiwyg within modal window
       */
      if (!state.initialized) {
        setState((prevState) => ({ ...prevState, initialized: true }));
      }
    }, []);

    return (
      <>
        <Form
          ref={formRef}
          onFinish={handleSubmit}
          onFinishFailed={() => validateHTML(editorRef.current?.returnContent())}
          requiredMark={false}
          onValuesChange={onValuesChange}
          initialValues={initialValues}
          className="email-send-form"
        >
          <Tabs
            defaultActiveKey="1"
            className="email-send-tabs"
            items={[
              {
                key: "1",
                label: t("Message"),
                children: (
                  <>
                    <Row justify="space-between">
                      {formRef.current && <TopBar form={formRef.current} toggleFilesModal={toggleFilesModal} />}
                      <div className="checkbox-cc-bcc">
                        <ReceiverCheckbox
                          key={"bcc"}
                          checked={state.bcc}
                          type={"bcc"}
                          value={"bcc"}
                          onChange={handleBccFieldChange}
                        />
                      </div>
                    </Row>
                    <Row gutter={8}>
                      <Col className="email-send-button">
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={isLoading}
                          faIcon={["fal", "paper-plane"]}
                          faIconSize="2x"
                          columnDirection={true}
                          style={{ minWidth: 80, height: 86, width: 120 }}
                        >
                          {t(buttonText)}
                        </Button>
                        <Button
                          type="default"
                          onClick={() => handleSubmit(formRef.current.getFieldsValue(), true)}
                          style={{ minWidth: 100, marginTop: 6, width: 120, fontSize: 12 }}
                        >
                          {t(draftButtonText)}
                        </Button>
                      </Col>
                      <Col flex="1">
                        <Form.Item
                          name="to"
                          rules={[
                            {
                              required: true,
                              message: t("Please provide a receiver"),
                            },
                            { validator: checkEmail },
                          ]}
                          className="mb-0 email-form-item"
                        >
                          <EmailSelect
                            className="select-to"
                            autoFocus={true}
                            field="to"
                            mode="tags"
                            placeholder={t("To")}
                          />
                        </Form.Item>
                        <Form.Item name="cc" rules={[{ validator: checkEmail }]} className="mb-0 email-form-item">
                          <EmailSelect field="cc" mode="tags" placeholder="Cc" />
                        </Form.Item>
                        <Form.Item
                          style={{
                            display: state.bcc ? "block" : "none",
                          }}
                          className="mb-0 email-form-item"
                          name="bcc"
                          rules={[{ validator: checkEmail }]}
                        >
                          <EmailSelect field="bcc" mode="tags" placeholder="Bcc" />
                        </Form.Item>
                        <Form.Item name="subject">
                          <Input
                            name="subject"
                            placeholder={t("Subject")}
                            suffix={
                              emailSubmitStore.data.importance === "HIGH" && (
                                <FontAwesomeIcon fixedWidth={true} color={"red"} icon={["fas", "exclamation"]} />
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    {state.initialized && (
                      <Form.Item className={classNames("email-send-textarea", { error: state.isMessageInvalid })}>
                        <EmailTextEditor
                          initialContent={initialData?.body}
                          initialData={initialEditorData}
                          ref={editorRef}
                        />
                      </Form.Item>
                    )}
                    {state.isMessageInvalid && (
                      <Text type="danger" style={{ marginTop: "-15px" }}>
                        {t("Please enter a text")}
                      </Text>
                    )}
                  </>
                ),
              },
              {
                key: "2",
                label: attachmentsLabel,
                style: { marginBottom: 15, height: 640 },
                children: (
                  <EmailAttachmentsTable
                    loading={emailSubmitStore.externalFilesLoading}
                    data={emailSubmitStore.fileList}
                    toggleFilesModal={toggleFilesModal}
                    removeFile={emailSubmitStore.deleteFile}
                  />
                ),
              },
            ]}
          />
        </Form>
      </>
    );
  }
);
